"use client";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

// --- Custom Zero-Dependency Strapi Blocks Renderer ---
const RenderInlineContent = ({ children }) => {
  if (!children) return null;

  return children.map((child, index) => {
    let textNode = <span key={index}>{child.text}</span>;

    if (child.bold) textNode = <strong key={index}>{textNode}</strong>;
    if (child.italic) textNode = <em key={index}>{textNode}</em>;
    if (child.underline) textNode = <u key={index}>{textNode}</u>;
    if (child.code) textNode = <code key={index} className="bg-gray-100 px-1 rounded">{textNode}</code>;

    if (child.type === "link") {
      return (
        <a
          key={index}
          href={child.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          <RenderInlineContent children={child.children} />
        </a>
      );
    }

    return textNode;
  });
};

const StrapiRichText = ({ content }) => {
  if (!Array.isArray(content) || content.length === 0) return null;

  return content.map((block, index) => {
    switch (block.type) {
      case "paragraph":
        return (
          <p key={index} className="mb-2">
            <RenderInlineContent children={block.children} />
          </p>
        );

      case "list": {
        const IsOrdered = block.format === "ordered";
        const ListTag = IsOrdered ? "ol" : "ul";
        const listClasses = IsOrdered
          ? "list-decimal pl-6 mb-3 space-y-1"
          : "list-disc pl-6 mb-3 space-y-1";

        return (
          <ListTag key={index} className={listClasses}>
            {block.children?.map((listItem, itemIndex) => (
              <li key={itemIndex}>
                <RenderInlineContent children={listItem.children} />
              </li>
            ))}
          </ListTag>
        );
      }

      case "heading": {
        const level = block.level || 3;
        const HeadingTag = `h${level}`;
        return (
          <HeadingTag key={index} className="font-semibold my-2 text-black">
            <RenderInlineContent children={block.children} />
          </HeadingTag>
        );
      }

      default:
        // Fallback for unknown block types
        return (
          <div key={index}>
            <RenderInlineContent children={block.children} />
          </div>
        );
    }
  });
};

// --- Main Accordion Component ---
export default function FaqAccordion({ faqData }) {
  const [openItem, setOpenItem] = useState(
    faqData && faqData.length > 0 ? faqData[0].id : null
  );

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
  };

  return (
    <div className="">
      {faqData?.map((item, index) => {
        const itemId = item.id;
        const isOpen = openItem === itemId;
        const displayNumber = String(index + 1).padStart(2, "0");

        const hasRichText =
          Array.isArray(item.RichText_Answer) && item.RichText_Answer.length > 0;

        return (
          <motion.div
            key={itemId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <div
              className={`border transition-all duration-500 ease-out mb-[12px]
                ${
                  isOpen
                    ? "bg-white border-blue-200/50 shadow-2xl shadow-blue-500/10 scale-[1.01] xs:scale-[1.02] ring-1 ring-blue-100/50"
                    : "bg-[#F9F9FD] border-gray-200/40 shadow-lg shadow-gray-500/5 hover:bg-white hover:border-blue-200/30 hover:shadow-xl hover:shadow-blue-500/8 hover:scale-[1.005] xs:hover:scale-[1.01] hover:ring-1 hover:ring-blue-100/30"
                }
              `}
            >
              {/* Question Row */}
              <div
                className="flex items-center justify-between px-[14px] py-[20px] md:px-[18px] md:py-[15px] cursor-pointer group"
                onClick={() => toggleItem(itemId)}
              >
                <div className="flex items-center flex-grow min-w-0 mr-2 xs:mr-3 sm:mr-4">
                  {/* Number */}
                  <div className="hidden md:flex transition-all duration-300 ease-in-out flex-shrink-0 mr-[15px] text-[24px] text-[#979CB1]">
                    {displayNumber}
                  </div>

                  {/* Question */}
                  <p
                    className="transition-all duration-300 ease-in-out text-black text-[20px] py-[10px]"
                    style={{ fontFamily: "saans-medium" }}
                  >
                    {item.Question}
                  </p>
                </div>

                {/* Toggle Button */}
                <motion.button
                  type="button"
                  aria-label={isOpen ? "Collapse answer" : "Expand answer"}
                  aria-expanded={isOpen}
                  className={`
                    w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 
                    rounded-full border-none transition-all duration-300 ease-in-out
                    flex items-center justify-center flex-shrink-0
                    ${
                      isOpen
                        ? "bg-[#0475B3] text-white shadow-lg shadow-blue-600/25 ring-1 xs:ring-2 ring-blue-200/50"
                        : "bg-white text-slate-400 shadow-md shadow-gray-500/10 hover:bg-[#0475B3] hover:text-white hover:shadow-lg hover:shadow-[#0475B3] hover:ring-1 xs:hover:ring-2 hover:ring-[#0475B3] ring-1 ring-gray-200/30"
                    }
                  `}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <AnimatePresence mode="wait">
                    {isOpen ? (
                      <motion.div
                        key="minus"
                        initial={{ opacity: 0, rotate: -90 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: 90 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Minus
                          className="w-[14px] h-[14px] sm:w-[18px] sm:h-[18px]"
                          strokeWidth={2.5}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="plus"
                        initial={{ opacity: 0, rotate: 90 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: -90 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Plus
                          className="w-[14px] h-[14px] sm:w-[18px] sm:h-[18px]"
                          strokeWidth={2.5}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>

              {/* Answer - Animated */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <motion.div
                      className="mb-[30px] sm:text-[16px] text-black text-[14px] leading-[22px] px-[14px] md:px-[18px]"
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      style={{ fontFamily: "saans-regular" }}
                    >
                      {hasRichText ? (
                        <StrapiRichText content={item.RichText_Answer} />
                      ) : (
                        <p>{item.Answer}</p>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}